import React, { useEffect, useState, useMemo } from 'react';
import Grid from "@mui/material/Grid";
import MDBox from "components/atoms/MDBox/MDBox";
import LegacyTriageDataTable, {
  TableColumnType,
  TableSecondaryAction,
} from "components/organisms/LegacyTriageDataTable/LegacyTriageDataTable";
import { Breakpoints, MUIColors } from 'models/StyleModels';
import Alert from 'components/atoms/Alert/Alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import colors from 'assets/theme/base/colors';
import { usePatientData } from 'hooks/usePatientData';
import { Patient } from 'models/PatientModels';
import { useAuth } from 'hooks/useAuth';
import ActionSlugs from 'services/PermissionsService';
import "./LegacyTriageListPage.css";
import "react-datepicker/dist/react-datepicker.css";
import AddPatientModal from 'components/organisms/AddPatientModal/AddPatientModal';
import MDButton from 'components/atoms/MDButton/MDButton';
import { faEye, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import { RouteKeys } from 'models/RouteModels';
import { FLOW, useNavigation } from 'hooks/useNavigation';
import Spinner from 'components/molecules/Spinner/Spinner';
import { Typography } from '@mui/material';
import { User } from 'models/UserModels';
import { TriageProStatus, TriageXrayStatus } from 'models/AppointmentModels';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';
import { AxiosResponse } from 'axios';
import DateDisplay from 'components/atoms/DateDisplay/DateDisplay';
import TriageFlowLayout from 'components/templates/TriageFlowLayout/TriageFlowLayout';
import { useOrgData } from 'hooks/useOrgData';
import { Org } from 'models/OrgModels';
import { NameCellValue } from './LegacyTriagePage.styles';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { usePaginatedAppointmentData } from 'hooks/usePaginatedAppointmentData';

type RefetchFunction<TData> = <TPageData>(
  options?: RefetchOptions & RefetchQueryFilters<TPageData>
) => Promise<QueryObserverResult<TData, unknown>>;

interface ITriageListContentProps {
  allPatients: Patient[];
  user: User;
  org: Org;
  refetchAllPatients: RefetchFunction<AxiosResponse<{
    patients: Patient[];
  }> | undefined>;
}

const TriageListContent: React.FC<ITriageListContentProps> = ({
  allPatients,
  user,
  org,
  refetchAllPatients
}) => {
  const [addPatientModalOpen, setAddPatientModalOpen] = useState<boolean>(false);
  const [editPatientModalOpen, setEditPatientModalOpen] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const { navigateToBaseRouteWithAptmtDetails } = useNavigation();
  const { breakpointBreached: lgBreakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.LARGE });

  // Use paginated appointment data
  const {
    allAppointments,
    refetchAppointments
  } = usePaginatedAppointmentData({
    limit: 100, // Fetch more appointments per page since we need all of them
    polling: true, // Keep polling for updates
  });

  // Create a map of patient_id to appointment for easier lookup
  const appointmentsByPatientId = useMemo(() => {
    return allAppointments.reduce((acc, appointment) => {
      if (appointment.patient_id) {
        acc[appointment.patient_id] = appointment;
      }
      return acc;
    }, {} as Record<string, any>);
  }, [allAppointments]);

  const formatXrayStatus = (status: TriageXrayStatus) => {
    const commonStyles = { fontSize: '1rem' }
    switch (status) {
      case TriageXrayStatus.NO_XRAYS:
        return <Typography style={{ color: colors.error.main, ...commonStyles }}>No X-Rays</Typography>
      case TriageXrayStatus.XRAYS_MISSING:
        return <Typography style={{ color: colors.error.main, ...commonStyles }}>X-Rays Missing</Typography>
      case TriageXrayStatus.READY_FOR_REVIEW:
        return <Typography style={{ color: colors.warning.main, ...commonStyles }}>Ready for Review</Typography>
      case TriageXrayStatus.COMPLETED:
        return <Typography style={{ color: colors.success.main, ...commonStyles }}>Completed</Typography>
      case TriageXrayStatus.ANALYZING:
        return <Typography style={{ color: colors.warning.main, ...commonStyles }}>Analyzing...</Typography>
      case TriageXrayStatus.ANALYSIS_FAILED:
        return <Typography style={{ color: colors.error.main, ...commonStyles }}>Analysis Failed</Typography>
      default:
        return <Typography style={{ color: colors.error.main, ...commonStyles }}>No X-Rays</Typography>
    }
  }

  const formatPROStatus = (status: TriageProStatus) => {
    const commonStyles = { fontSize: '1rem' }
    switch (status) {
      case TriageProStatus.COMPLETED:
        return <Typography style={{ color: colors.success.main, ...commonStyles }}>Completed</Typography>
      default:
        return <Typography style={{ color: colors.error.main, ...commonStyles }}>Incomplete</Typography>
    }
  }

  const patientTableData = allPatients?.sort((a, b) => (new Date(b.created_at.replace(/ /g, "T")) as any) - (new Date(a.created_at.replace(/ /g, "T")) as any)).map((patient) => ({
    json: patient,
    patient_name: `${patient.last_name}, ${patient.first_name}`,
    id_number: patient?.external_id || '-',
    data: 'x-ray',
    status: TriageXrayStatus.NO_XRAYS,
  })) || [];

  const ExamRoomButton = (row: any) => {
    const appointment = appointmentsByPatientId[row.original.json.patient_id];

    if (
      !appointment ||
      [TriageXrayStatus.NO_XRAYS, TriageXrayStatus.XRAYS_MISSING].includes(appointment.xray_status)) {

      return (
        <MDButton
          variant='contained'
          size={lgBreakpointBreached ? 'small' : 'medium'}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            navigateToBaseRouteWithAptmtDetails({
              routeKey: RouteKeys.MATCH_XRAYS,
              appointmentId: appointment.appointment_id,
              appointmentDateISOString: appointment.appointment_date,
            })

          }}
          color={MUIColors.SECONDARY}
        >
          <FontAwesomeIcon color={colors.white.focus} icon={faPlus} style={{ marginRight: '10px' }} />
          Add
        </MDButton>
      )
    }

    if (appointment?.xray_status === TriageXrayStatus.READY_FOR_REVIEW || appointment?.xray_status === TriageXrayStatus.COMPLETED) {

      return (
        <MDButton
          variant='contained'
          size={lgBreakpointBreached ? 'small' : 'medium'}
          onClick={() => {
            navigateToBaseRouteWithAptmtDetails({
              routeKey: RouteKeys.EXAM_ROOM_ALL_JOINTS,
              appointmentId: appointment.appointment_id,
              appointmentDateISOString: appointment.appointment_date,
              addtlQueryParams: { flow: FLOW.TRIAGE }
            })
          }}
          color={MUIColors.SECONDARY}
        >
          <FontAwesomeIcon color={colors.white.focus} icon={faEye} style={{ marginRight: '10px' }} />
          Exam Room
        </MDButton>
      )
    }

    return null
  }

  const patientColumns = [
    {
      Header: "Created At", accessor: "created_at", width: "10%", align: "left",
      Cell: ({ row }: any) => { return <DateDisplay isoString={row.original.json.created_at} showTime={false} /> },
      canSort: true,
    },
    {
      Header: "Patient Name",
      accessor: "patient_name",
      width: "20%",
      align: "left",
      canSort: true,
      Cell: ({ row }: any) => {
        const appointment = appointmentsByPatientId[row.original.json.patient_id];
        const isDisabled = !appointment;
        return (
          <div style={{ width: `${lgBreakpointBreached ? '60px' : ''}` }}>
            <NameCellValue
              disabled={isDisabled}
              onClick={() => {
                if (appointment) {
                  setSelectedPatient(row.original.json)
                  setEditPatientModalOpen(true)
                }
              }}>{row.values.patient_name}</NameCellValue>
          </div>)
      }
    },
    {
      Header: "ID Number",
      accessor: "id_number",
      align: "left",
      canSort: true,
      Cell: ({ row }: any) => {
        return <div>{row.original.json.external_id}</div>
      }
    },
    {
      Header: "Data", accessor: "data", align: "left",
      Cell: ({ row }: any) => (
        <MDBox display="grid" gap="1rem" gridAutoRows="2.5rem 2.5rem" alignItems="center">
          <MDBox>
            X-Rays
          </MDBox >

          {org.use_pros && <MDBox>
            PROs
          </MDBox >}

          {lgBreakpointBreached ?
            <div>
              {appointmentsByPatientId[row.original.json.patient_id]?.xray_status &&
                appointmentsByPatientId[row.original.json.patient_id]?.xray_status !== TriageXrayStatus.NO_XRAYS && (
                  <MDButton
                    variant='outlined'
                    onClick={() => { }}
                    size='small'
                    color={MUIColors.SECONDARY}
                    onClickCapture={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const appointment = appointmentsByPatientId[row.original.json.patient_id];
                      navigateToBaseRouteWithAptmtDetails({
                        routeKey: RouteKeys.MATCH_XRAYS,
                        appointmentId: appointment.appointment_id,
                        appointmentDateISOString: appointment.appointment_date,
                      })
                    }}
                  >
                    <FontAwesomeIcon color={colors.secondary.main} icon={faPen} style={{ marginRight: '10px' }} />
                    Edit
                  </MDButton>
                )}
            </div> : ''}

        </MDBox >
      )
    },
    {
      Header: "Status", accessor: "status", align: "left",
      Cell: ({ row }: any) => (
        <MDBox display="grid" gap="1rem" gridAutoRows="2.5rem 2.5rem" alignItems="center">
          <MDBox style={{ color: colors.error.main }}>
            {formatXrayStatus(appointmentsByPatientId[row.original.json.patient_id]?.xray_status)}
          </MDBox >

          {org.use_pros &&
            <MDBox style={{ color: colors.error.main }}>
              {formatPROStatus(appointmentsByPatientId[row.original.json.patient_id]?.pro_status)}
            </MDBox >
          }

          {lgBreakpointBreached ? ExamRoomButton(row) : ''}

        </MDBox >)
    }
  ];

  const fullScreenPatientColumns = [
    ...patientColumns,
    {
      Header: "", accessor: "a", align: "right",
      Cell: ({ row }: any) => {
        const appointment = appointmentsByPatientId[row.original.json.patient_id];
        return (<MDBox display="grid" gap="1rem" gridAutoRows="2.5rem 2.5rem">
          <div>
            {appointment?.xray_status && appointment?.xray_status !== TriageXrayStatus.NO_XRAYS && (
              <MDButton
                variant='outlined'
                onClick={() => { }}
                color={MUIColors.SECONDARY}
                onClickCapture={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  navigateToBaseRouteWithAptmtDetails({
                    routeKey: RouteKeys.MATCH_XRAYS,
                    appointmentId: appointment.appointment_id,
                    appointmentDateISOString: appointment.appointment_date,
                  })
                }}
              >
                <FontAwesomeIcon color={colors.secondary.main} icon={faPen} style={{ marginRight: '10px' }} />
                Edit
              </MDButton>
            )}
          </div>

          {org.use_pros &&
            <div>
              <MDButton
                variant='outlined'
                onClick={() => { }}
                color={MUIColors.SECONDARY}
                onClickCapture={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  navigateToBaseRouteWithAptmtDetails({
                    routeKey: RouteKeys.TRIAGE_SURVEY,
                    appointmentId: appointment.appointment_id,
                    appointmentDateISOString: appointment.appointment_date,
                    addtlQueryParams: {
                      view: 'desktop',
                    },
                  })
                }}
              >
                <FontAwesomeIcon color={colors.secondary.main} icon={faPen} style={{ marginRight: '10px' }} />
                Edit
              </MDButton>
            </div>
          }

        </MDBox>)
      }
    },
    {
      Header: "", accessor: "b", align: "right", Cell: ({ row }: any) => { return ExamRoomButton(row) }
    }
  ]

  interface ModeData {
    tableData: Record<any, any>[];
    columns: TableColumnType[];
    handleRowClick(args?: any): void;
    tableSecondaryAction: TableSecondaryAction | null,
    header: string;
    headerAction?: React.ReactNode;
    createSuccessAlertText: string;
  }

  const activeData: ModeData = {
    tableData: patientTableData,
    columns: lgBreakpointBreached ? patientColumns : fullScreenPatientColumns,
    createSuccessAlertText: 'Patient successfully added.',
    handleRowClick: (row) => { },
    header: 'Triage List',
    tableSecondaryAction: user?.can(ActionSlugs.CREATE$PATIENT)
      ? {
        label: 'Add Patient',
        action: () => setAddPatientModalOpen(true),
      }
      : null
  }

  return (
    <>
      <Alert show={showAlert} handleClose={() => setShowAlert(false)}>
        {activeData.createSuccessAlertText}
      </Alert>

      {addPatientModalOpen && (
        <AddPatientModal
          onSuccess={() => {
            setAddPatientModalOpen(false);
          }}
          closeModal={() => setAddPatientModalOpen(false)} />
      )}

      {editPatientModalOpen && selectedPatient && (
        <AddPatientModal
          isEdit
          onSuccess={async () => {
            await refetchAppointments();
            setEditPatientModalOpen(false);
          }}
          appointmentDate={appointmentsByPatientId[selectedPatient.patient_id]?.appointment_date}
          appointmentId={appointmentsByPatientId[selectedPatient.patient_id]?.appointment_id}
          patient={selectedPatient}
          closeModal={() => setEditPatientModalOpen(false)} />
      )}

      <TriageFlowLayout title="Triage List" singlePatient={null}>
        <MDBox pb={3}>
          <Grid item xs={12} pt={2}>
            <LegacyTriageDataTable
              table={{ columns: activeData.columns, rows: activeData.tableData }}
              canSearch={true}
              isSorted={[true, true, true, false, false, false]}
              pagination={{ variant: "contained", color: "secondary" }}
              sx={{ padding: "0 2.25rem", float: "left" }}
              handleRowClick={activeData.handleRowClick}
              secondaryAction={activeData.tableSecondaryAction}
            />
          </Grid>
        </MDBox>
      </TriageFlowLayout >
    </>
  );
};

const BatchXrayUploadFallback: React.FC = () => {
  return (
    <TriageFlowLayout title="Triage List" singlePatient={null}>
      <MDBox>
        <Spinner />
      </MDBox>
    </TriageFlowLayout>
  )
}

const LegacyTriageListPage: React.FC = () => {
  const { user } = useAuth();
  const { org } = useOrgData();
  const {
    allPatients,
    allPatientsLoading,
    refetchAllPatients
  } = usePatientData();

  const {
    allAppointments,
    appointmentsInitialLoading
  } = usePaginatedAppointmentData({
    limit: 100,
    polling: true
  });

  return (
    !(allPatientsLoading || appointmentsInitialLoading) &&
    allPatients && allAppointments && user && org
  ) ? (
    <TriageListContent
      refetchAllPatients={refetchAllPatients}
      allPatients={allPatients}
      user={user}
      org={org}
    />
  ) : (
    <BatchXrayUploadFallback />
  );
};

export default LegacyTriageListPage;
